/**
 * Page de gestion des places par séjour pour les admins.
 * Affiche les statistiques détaillées par tranche d'âge et partenaire avec filtres.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { sejourService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FunnelIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { notify } from '../components/NotificationContainer';
import logger from '../utils/logger';

const GestionPlaces = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sejours, setSejours] = useState([]);
  const [expandedSejours, setExpandedSejours] = useState(new Set());

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSejour, setSelectedSejour] = useState('all');
  const [filterComplet, setFilterComplet] = useState('all'); // all, complet, disponible

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadStatistiques();
  }, [user, navigate]);

  const loadStatistiques = async () => {
    try {
      setLoading(true);
      const response = await sejourService.getStatistiquesPlacesTous();
      logger.log('[GestionPlaces] Données reçues de l\'API:', response.data);

      // Log détaillé pour chaque séjour
      response.data.forEach(sejour => {
        logger.log(`[GestionPlaces] Séjour "${sejour.titre}":`);
        logger.log(`  - Total places: ${sejour.total_places_prises} / ${sejour.total_places_disponibles}`);
        logger.log(`  - Nombre de tranches: ${sejour.tranches.length}`);
        sejour.tranches.forEach(tranche => {
          logger.log(`    Tranche ${tranche.age_min}-${tranche.age_max}:`);
          logger.log(`      - Hors partenaires: ${tranche.inscriptions_hors_partenaires} / ${tranche.places_disponibles}`);
          logger.log(`      - Partenaires:`, tranche.inscriptions_partenaires_par_partenaire);
        });
      });

      setSejours(response.data);

      // Expand premier séjour par défaut
      if (response.data.length > 0) {
        setExpandedSejours(new Set([response.data[0].id]));
      }
    } catch (error) {
      logger.error('Erreur lors du chargement des statistiques:', error);
      notify.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  // Filtrage des séjours
  const filteredSejours = useMemo(() => {
    return sejours.filter(sejour => {
      // Filtre par recherche textuelle
      if (searchTerm && !sejour.titre.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      // Filtre par séjour spécifique
      if (selectedSejour !== 'all' && sejour.id !== parseInt(selectedSejour)) {
        return false;
      }

      // Filtre par disponibilité
      if (filterComplet === 'complet' && sejour.total_places_prises < sejour.total_places_disponibles) {
        return false;
      }
      if (filterComplet === 'disponible' && sejour.total_places_prises >= sejour.total_places_disponibles) {
        return false;
      }

      return true;
    });
  }, [sejours, searchTerm, selectedSejour, filterComplet]);

  const toggleSejourExpanded = (sejourId) => {
    const newExpanded = new Set(expandedSejours);
    if (newExpanded.has(sejourId)) {
      newExpanded.delete(sejourId);
    } else {
      newExpanded.add(sejourId);
    }
    setExpandedSejours(newExpanded);
  };

  const expandAll = () => {
    setExpandedSejours(new Set(filteredSejours.map(s => s.id)));
  };

  const collapseAll = () => {
    setExpandedSejours(new Set());
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedSejour('all');
    setFilterComplet('all');
  };

  const hasActiveFilters = searchTerm || selectedSejour !== 'all' || filterComplet !== 'all';

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      {/* En-tête */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <ChartBarIcon className="h-8 w-8 mr-3 text-primary-600" />
          Gestion des Places
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Visualisez et gérez la disponibilité des places par séjour et tranche d'âge
        </p>
      </div>

      {/* Barre de filtres */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Filtres</h2>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="ml-4 text-sm text-primary-600 hover:text-primary-800 flex items-center"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Réinitialiser
              </button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={expandAll}
              className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded hover:bg-gray-100"
            >
              Tout déplier
            </button>
            <button
              onClick={collapseAll}
              className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded hover:bg-gray-100"
            >
              Tout replier
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Recherche textuelle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rechercher un séjour
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nom du séjour..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Sélection de séjour */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Séjour spécifique
            </label>
            <select
              value={selectedSejour}
              onChange={(e) => setSelectedSejour(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Tous les séjours</option>
              {sejours.map((sejour) => (
                <option key={sejour.id} value={sejour.id}>
                  {sejour.titre}
                </option>
              ))}
            </select>
          </div>

          {/* Filtre disponibilité */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Disponibilité
            </label>
            <select
              value={filterComplet}
              onChange={(e) => setFilterComplet(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Tous</option>
              <option value="disponible">Avec places disponibles</option>
              <option value="complet">Complets</option>
            </select>
          </div>
        </div>

        {/* Compteur de résultats */}
        <div className="mt-4 text-sm text-gray-600">
          {filteredSejours.length} séjour{filteredSejours.length > 1 ? 's' : ''} affiché{filteredSejours.length > 1 ? 's' : ''}
          {hasActiveFilters && ` sur ${sejours.length} au total`}
        </div>
      </div>

      {/* Liste des séjours */}
      {filteredSejours.length === 0 ? (
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun séjour trouvé</h3>
          <p className="mt-1 text-sm text-gray-500">
            {hasActiveFilters
              ? 'Essayez de modifier vos critères de recherche.'
              : 'Il n\'y a actuellement aucun séjour actif avec des tranches d\'âge.'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="mt-4 text-sm text-primary-600 hover:text-primary-800"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSejours.map((sejour) => {
            const isExpanded = expandedSejours.has(sejour.id);
            const tauxRemplissage = sejour.total_places_disponibles > 0
              ? (sejour.total_places_prises / sejour.total_places_disponibles) * 100
              : 0;

            return (
              <div key={sejour.id} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow">
                {/* En-tête du séjour - Cliquable */}
                <div
                  className="px-6 py-4 bg-gradient-to-r from-primary-50 to-blue-50 border-b border-primary-200 cursor-pointer hover:from-primary-100 hover:to-blue-100 transition-colors"
                  onClick={() => toggleSejourExpanded(sejour.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      {isExpanded ? (
                        <ChevronDownIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <h2 className="text-xl font-bold text-gray-900">
                          {sejour.titre}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                          {new Date(sejour.date_debut).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                          {' → '}
                          {new Date(sejour.date_fin).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-6">
                      {/* Barre de progression */}
                      <div className="hidden md:block w-32">
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span>Remplissage</span>
                          <span className="font-semibold">{tauxRemplissage.toFixed(0)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              tauxRemplissage >= 100
                                ? 'bg-red-500'
                                : tauxRemplissage >= 80
                                ? 'bg-orange-500'
                                : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(tauxRemplissage, 100)}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Total des places */}
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary-700">
                          {sejour.total_places_prises} / {sejour.total_places_disponibles}
                        </div>
                        <div className="text-xs text-gray-600">Places (hors partenaires)</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contenu détaillé */}
                {isExpanded && (
                  <div className="px-6 py-4">
                    {/* Tranches d'âge */}
                    <div className="space-y-4">
                      {sejour.tranches && sejour.tranches.length > 0 ? (
                        sejour.tranches.map((tranche, index) => {
                          const totalInscriptionsPartenaires = Object.values(
                            tranche.inscriptions_partenaires_par_partenaire || {}
                          ).reduce((sum, count) => sum + count, 0);

                          const placesRestantes = tranche.places_disponibles - tranche.inscriptions_hors_partenaires;

                          return (
                            <div key={index} className="border-l-4 border-primary-500 bg-gray-50 rounded-r-lg pl-4 pr-4 py-3">
                              {/* Ligne de tranche */}
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-4">
                                  <span className="font-semibold text-gray-900 text-lg">
                                    {tranche.age_min} - {tranche.age_max} ans
                                  </span>
                                  <span className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full">
                                    {tranche.prix} €
                                  </span>
                                </div>
                                <div className="flex items-center space-x-3">
                                  <div className="relative group flex items-center">
                                    <span
                                      className={`px-3 py-1 text-sm font-medium rounded-full ${
                                        placesRestantes <= 0
                                          ? 'bg-red-100 text-red-800'
                                          : placesRestantes <= 5
                                          ? 'bg-orange-100 text-orange-800'
                                          : 'bg-green-100 text-green-800'
                                      }`}
                                    >
                                      {placesRestantes > 0 ? `${placesRestantes} restantes` : 'Complet'}
                                    </span>
                                    <InformationCircleIcon className="h-4 w-4 ml-1 text-gray-400 cursor-help flex-shrink-0" />
                                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-72 bg-gray-800 text-white text-xs rounded-lg p-2 z-20 shadow-lg">
                                      Nombre de places restantes par rapport au nombre de places ouvertes à l'inscription, hors partenaires.
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Places partenaires */}
                              {tranche.places_partenaires > 0 && (
                                <div className="mt-3 ml-2 p-3 bg-purple-50 rounded-lg">
                                  {/* Places hors partenaires */}
                                  <div className="text-sm font-semibold text-purple-900 mb-2 flex items-center">
                                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                                    Places hors partenaires : {tranche.inscriptions_hors_partenaires} / {tranche.places_disponibles}
                                  </div>

                                  {/* Places partenaires */}
                                  <div className="text-sm font-semibold text-purple-900 mb-2 flex items-center">
                                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                                    PLACES PARTENAIRES
                                  </div>

                                  {Object.keys(tranche.inscriptions_partenaires_par_partenaire || {}).length > 0 ? (
                                    <div className="space-y-1 ml-4">
                                      {Object.entries(tranche.inscriptions_partenaires_par_partenaire).map(
                                        ([partenaireNom, count]) => (
                                          <div key={partenaireNom} className="flex items-center text-sm">
                                            <span className="font-medium text-purple-800 w-32">
                                              {partenaireNom}
                                            </span>
                                            <span className="text-gray-400 mx-2">•</span>
                                            <span className="text-purple-700">
                                              {tranche.age_min}-{tranche.age_max} ans :
                                            </span>
                                            <span className="ml-2 font-bold text-purple-900">
                                              {count} place{count > 1 ? 's' : ''}
                                            </span>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-sm text-gray-600 ml-4 italic">
                                      Aucune inscription partenaire (quota : {tranche.places_partenaires})
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Total inscrits (hors partenaires + partenaires) */}
                              <div className="mt-3 text-sm font-semibold text-gray-700">
                                Total inscrits : {tranche.inscriptions_hors_partenaires + totalInscriptionsPartenaires}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-gray-500 text-center py-4">
                          Aucune tranche d'âge configurée pour ce séjour
                        </div>
                      )}
                    </div>

                    {/* Note d'information */}
                    <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-5 w-5 text-blue-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-blue-700">
                            <strong>Note :</strong> Les inscriptions via partenaires sociaux ne sont pas
                            limitées par le nombre de places disponibles. Le quota affiché est indicatif.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GestionPlaces;
